import * as React from "react"
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles"
import { CssBaseline, GlobalStyles } from "@mui/material"

let customTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1200,
      xl: 1920,
    },
  },
  // Colors used via theme
  palette: {
    background: {
      default: "#FCFCFC",
      white: "#fff",
      gradient: `linear-gradient(185deg, #FDE047,  #CA8A04)`,
    },
    primary: {
      50: "#FFFBEB",
      100: "#FEF1C7",
      200: "#FEEBAA",
      300: "#F7DC8A",
      400: "#F5D273",
      500: "#F0B924",
      600: "#D99A06",
      700: "#B47E08",
      800: "#87560D",
      900: "#6B4B0B",
      main: "#F5D273",
      contrastText: "#262626",
      hover: "rgba(252, 247, 227, 0.7)",
    },
    dataViz: {
      50: "#F5D273",
      100: "#F28E2C",
      200: "#77B8B2",
      300: "#E15758",
      400: "#58A14E",
      500: "#4D79A7",
      600: "#AF7AA1",
      700: "#FF9DA7",
      800: "#9B755F",
      900: "#B9B0AB",
    },
    green: {
      // From Tailwind Emerald
      50: "#ECFDF5",
      100: "#D1FAE5",
      200: "#A7F3D0",
      300: "#6EE7B7",
      400: "#34D399",
      500: "#10B981",
      600: "#059669",
      700: "#047857",
      800: "#065F46",
      900: "#064E3B",
    },
    red: {
      50: "#FEF2F2",
      100: "#FEE2E2",
      200: "#FECACA",
      300: "#FCA5A5",
      400: "#F87171",
      500: "#EF4444",
      600: "#DC2626",
      700: "#B91C1C",
      800: "#991B1B",
      900: "#7F1D1D",
      main: "#EF4444",
      contrastText: "#FFF",
    },
    fuchsia: {
      50: "#FDF4FF",
      100: "#FAE8FF",
      200: "#F5D0FE",
      300: "#F0ABFC",
      400: "#E879F9",
      500: "#D946EF",
      600: "#C026D3",
      700: "#A21CAF",
      800: "#86198F",
      900: "#701A75",
    },
    violet: {
      50: "#f5f3ff",
      100: "#ede9fe",
      200: "#ddd6fe",
      300: "#c4b5fd",
      400: "#a78bfa",
      500: "#8b5cf6",
      600: "#7c3aed",
      700: "#6d28d9",
      800: "#5b21b6",
      900: "#4c1d95",
    },
    blue: {
      // comes from Tailwind Sky color
      50: "#F0F9FF",
      100: "#E0F2FE",
      200: "#BAE6FD",
      300: "#7DD3FC",
      400: "#38BDF8",
      500: "#0EA5E9",
      600: "#0284C7",
      700: "#0369A1",
      800: "#075985",
      900: "#0C4A6E",
    },
    gray: {
      50: "#FAFAF9",
      100: "#F5F5F5",
      200: "#E5E5E5",
      300: "#D4D4D4",
      400: "#a3a3a3",
      500: "#737373",
      600: "#525252",
      700: "#404040",
      800: "#262626",
      900: "#171717",
      A100: "#f5f5f5",
      A200: "#eeeeee",
      A400: "#bdbdbd",
      A700: "#616161",
      main: "#737373",
    },
    warmGray: {
      50: "#FAFAF9",
      100: "#F5F5F5",
      200: "#E5E5E5",
      300: "#D4D4D4",
      400: "#a3a3a3",
      500: "#737373",
      600: "#525252",
      700: "#404040",
      800: "#262626",
      900: "#171717",
    },
  },
  // these are in a weird order, because MUI requires a whole array but we only want like 5 or 6
  shadows: [
    "none",
    "0 1px 3px rgb(44 42 38 / 7%)", // sm
    "0 1px 3px rgb(44 42 38 / 7%), 0 4px 16px rgb(44 42 38 / 6%)",
    "0 1px 4px rgb(44 42 38 / 7%), 0 4px 24px rgb(44 42 38 / 7%)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)", // md
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", // lg
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)", // xl
    "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  ],
  shape: {
    borderRadius: 6,
  },
  transition: property => `0.2s ${property} ease-in-out`,
  typography: {
    fontSize: 14,
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
    "5xl": "3rem",
    "6xl": "3.75rem",
    "7xl": "4.5rem",
    "8xl": "6rem",
    "9xl": "8rem",
    fontFamily: [
      '"Inter"',
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    h1: {
      fontSize: "2rem",
      fontWeight: 800,
      lineHeight: 78 / 70,
    },
    h2: {
      fontSize: "1.25rem",
      fontWeight: 700,
      lineHeight: 44 / 36,
    },
    h3: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 44 / 36,
      letterSpacing: 0,
    },
    h4: {
      fontSize: "0.75rem",
      lineHeight: 42 / 28,
      letterSpacing: 0,
    },
    h5: {
      fontSize: "0.5rem",
      lineHeight: 36 / 24,
      letterSpacing: 0,
    },
    h6: {
      fontSize: "0.5rem",
      lineHeight: 30 / 20,
      letterSpacing: 0,
    },
  },
})

// step 2 to use custom theme values:
customTheme = createTheme(customTheme, {
  border: `1px solid ${customTheme.palette.warmGray["200"]}`,
  // Themed component style overrides
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          // eyeballed to match other inputs
          paddingBottom: `6px !important `,
          paddingLeft: `12px !important `,
          paddingRight: `32px !important `,
          paddingTop: `6px !important `,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === "contained" &&
            ownerState.color === "primary" && {
              backgroundColor: customTheme.palette.primary["400"],
              transition: customTheme.transition(`all`),
              fontWeight: 600,
              "&:hover": {
                filter: "contrast(1.1)",
              },
            }),
          cursor: "pointer",
          borderRadius: customTheme.shape.borderRadius * 1.25,
          textTransform: "inherit",
          letterSpacing: 0.2,
        }),
      },
      variants: [
        {
          props: { variant: "gradient" },
          style: {
            display: "flex",
            border: `1px solid ${customTheme.palette.primary["400"]}`,
            background: customTheme.palette.background.gradient,
            color: customTheme.palette.background.white,
            boxShadow: customTheme.shadows[2],
            transition: customTheme.transition(`all`),
            textShadow: `${customTheme.palette.primary["500"]} 0px 1px 2px`,
            "&:hover": {
              filter: "contrast(1.25)",
            },
          },
        },
      ],
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          background: customTheme.palette.gray["100"],
          padding: `${customTheme.spacing(1)} ${customTheme.spacing(2)}`,
          justifyContent: "flex-end",
          color: customTheme.palette.gray["500"],
          transition: customTheme.transition(`background`),
          fontWeight: "500",
          "&:hover": {
            background: customTheme.palette.gray["50"],
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: customTheme.typography.xl,
          fontWeight: "500",
          color: customTheme.palette.gray["700"],
        },
        action: {
          alignSelf: "center",
          marginRight: 0,
          fontSize: customTheme.typography.sm,
          color: customTheme.palette.gray["500"],
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { variant: "inverted" },
          style: {
            color: customTheme.palette.primary["500"],
            fontWeight: "500",
            transition: customTheme.transition(`all`),
            background: customTheme.palette.primary["50"],
            "&:hover": {
              color: customTheme.palette.primary["600"],
              background: customTheme.palette.primary["100"],
            },
          },
        },
      ],
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: customTheme.spacing(4),
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: customTheme.shape.borderRadius * 1.5,
        },
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          borderColor: customTheme.palette.gray["200"],
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: customTheme.shape.borderRadius,
          boxShadow: `inset 0 1px 3px 0 rgba(0,0,0,0.025)`,
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: "filled",
      },
      styleOverrides: {
        root: {
          borderColor: customTheme.palette.warmGray["200"],
          minWidth: 100,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: customTheme.typography.sm,
          fontWeight: "600",
          color: customTheme.palette.warmGray["600"],
          "&.Mui-focused": {
            color: "inherit !important",
          },
        },
      },
    },
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: customTheme.shape.borderRadius,
          "&.Mui-focused": {
            backgroundColor: customTheme.palette.warmGray["100"],
          },
        },
      },
      variants: [
        {
          props: {
            variant: "small",
          },
          style: {
            ".MuiFilledInput-input": {
              paddingTop: customTheme.spacing(0.75),
              paddingBottom: customTheme.spacing(0.75),
            },
          },
        },
      ],
    },
    MuiGridFilterForm: {
      styleOverrides: {
        closeIcon: {
          minWidth: 30,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          ".MuiListItemText-primary": {
            fontWeight: "500",
            color: customTheme.palette.gray["700"],
          },
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        elevation: 2,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: customTheme.spacing(0.5),
          paddingBottom: customTheme.spacing(0.5),
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: customTheme.palette.warmGray["200"],
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderColor: customTheme.palette.gray["200"],
          borderWidth: 1,
          borderStyle: "solid",
        },
      },
    },
    MuiSelect: {
      variants: [
        {
          props: { variant: "small" },
          style: {
            paddingTop: 0,
          },
        },
      ],
      styleOverrides: {
        select: {
          minWidth: 100,
          borderRadius: customTheme.shape.borderRadius,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: 2,
            transitionDuration: "300ms",
            "&.Mui-checked": {
              transform: "translateX(16px)",
              color: "#fff",
              "& + .MuiSwitch-track": {
                backgroundColor: "#65C466",
                opacity: 1,
                border: 0,
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.5,
              },
            },
            "&.Mui-focusVisible .MuiSwitch-thumb": {
              color: "#33cf4d",
              border: "6px solid #fff",
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: customTheme.palette.grey[100],
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.7,
            },
          },
          "& .MuiSwitch-thumb": {
            boxSizing: "border-box",
            width: 22,
            height: 22,
          },
          "& .MuiSwitch-track": {
            borderRadius: 26 / 2,
            backgroundColor: "#E9E9EA",
            opacity: 1,
            // transition: customTheme.transitions.create(['background-color'], {
            //   duration: 500,
            // }),
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          paddingLeft: 0,
          paddingRight: 0,
          minWidth: 60,
        },
        selected: {},
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          gap: customTheme.spacing(4),
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "inherit",
          "&.Mui-selected": {
            color: customTheme.palette.primary["700"],
          },
          "&.Mui-disabled": {
            color: customTheme.palette.gray["300"],
          },
        },
        sizeSmall: {
          paddingTop: customTheme.spacing(0.5),
          paddingBottom: customTheme.spacing(0.5),
          paddingLeft: customTheme.spacing(1),
          paddingRight: customTheme.spacing(1),
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: customTheme.palette.gray["800"],
      },
    },
  },
})

export { customTheme }

export const ThemeProvider = props => {
  const theme = customTheme

  return (
    <MuiThemeProvider theme={theme}>
      {/* Set global MUI styles */}
      <GlobalStyles
        styles={{
          html: {
            minHeight: "100vh",
          },
          body: {
            minHeight: "100vh",
          },
          "#__next": {
            minHeight: "100vh",
          },
          ".Mui-focusVisible": {
            boxShadow: `0 0 0 3px ${theme.palette.primary["200"]} !important`,
          },
          ".MuiDataGrid-row": {
            backgroundColor: theme.palette.background.white,
          },
          ".MuiDataGrid-columnsContainer": {
            backgroundColor: theme.palette.warmGray["100"],
            borderTopLeftRadius: theme.shape.borderRadius,
            borderTopRightRadius: theme.shape.borderRadius,
          },
          ".MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.warmGray["100"],
            minHeight: "48px !important",
            maxHeight: "48px !important",
          },
          ".MuiDataGrid-virtualScroller": {
            marginTop: "48px !important",
          },
          ".MuiDataGrid-columnHeaderTitleContainer": {
            paddingLeft: "2px",
          },
          ".MuiDataGrid-cell--textRight": {
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "-0.05em",
          },
          ".MuiTabs-indicator": {
            display: "flex",
            justifyContent: "center",
            backgroundColor: "transparent",
          },
          ".MuiPickersDay-root": {
            borderRadius: `${theme.shape.borderRadius}px !important`,
          },
          ".MuiTablePagination-select": {
            paddingBottom: `1px !important`,
          },
          ".MuiBreadcrumbs-separator": {
            color: theme.palette.grey["400"],
          },
          // change styles of G2Plot tooltips used in Ant Design charts
          ".g2-tooltip": {
            border: `${theme.border} !important`,
            borderRadius: `${theme.shape.borderRadius} !important`,
            boxShadow: `${theme.shadows[1]} !important`,
            fontFamily: `${theme.typography.fontFamily} !important`,
          },
          ".daily-variant-stats-cell": {
            padding: "0 !important",
          },
          ".hot-toast": {
            padding: `${theme.spacing(1.5)} !important`,
            gap: theme.spacing(1.5),
            "[role='status']": {
              margin: 0,
            },
          },
        }}
      />
      <CssBaseline />
      {props.children}
    </MuiThemeProvider>
  )
}
